import React, { useEffect, useMemo, useState } from 'react';
import { fetchInspectionYear, YearRow, YearItem, YearItemStatus } from '../../services/inspectionsApi';
import { PlannedInfo } from './PlannedPanel';
import { intervalText } from './ScheduleEditor';
import { inGroup } from './common';
import { ui } from '../../styles/shared';
import { fmtDate } from '../../utils/format';

/**
 * Fliken "Årshjul": tolv månader i bredd, ett schema per rad, en prick per tillfälle.
 * Gjorda och öppna kontroller kommer från tabellen, kommande räknas fram från schemat.
 * Klick på en prick öppnar kontrollen; planerade tillfällen finns inte som kontroll ännu.
 */
interface Props {
    group: string;                 // '' = alla grupper
    refreshKey: number;
    onOpen: (id: number) => void;
    /** Öppnar en rond (alla platser) i panelen ovanför flikarna. */
    onOpenRound: (roundId: number) => void;
    /** Visar mallen för en planerad kontroll, bara att läsa. */
    onOpenPlanned: (planned: PlannedInfo) => void;
    onError: (msg: string) => void;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];

/** Färg och text per status. Ordningen är den som förklaringen visar. */
const STATUS: Record<YearItemStatus, { color: string; label: string; hollow?: boolean }> = {
    done:           { color: '#28a745', label: 'Gjord' },
    no_need:        { color: '#20c997', label: 'Utfört, inget behov' },
    deviation:      { color: '#fd7e14', label: 'Gjord, avvikelse' },
    action:         { color: '#fd7e14', label: 'Åtgärd pågår' },
    action_overdue: { color: '#dc3545', label: 'Åtgärd försenad' },
    pending:        { color: '#0d6efd', label: 'Att göra' },
    overdue:        { color: '#dc3545', label: 'Försenad' },
    skipped:        { color: '#6c757d', label: 'Hoppades över' },
    planned:        { color: '#0f3460', label: 'Planerad', hollow: true },
};
const LEGEND: YearItemStatus[] = ['done', 'no_need', 'deviation', 'action', 'pending', 'overdue', 'skipped', 'planned'];

const monthOf = (date: string) => Number(date.slice(5, 7)) - 1;

const Dot: React.FC<{ item: YearItem; onOpen: (id: number) => void; onOpenRound: (roundId: number) => void; onOpenPlanned: () => void }> = ({ item, onOpen, onOpenRound, onOpenPlanned }) => {
    const s = STATUS[item.status] ?? STATUS.planned;
    // Planerade prickar öppnar mallen att läsa; övriga öppnar kontrollen eller ronden.
    const clickable = true;
    const roundText = item.round_id ? ` · rond, ${item.round_done ?? 0} av ${item.round_total ?? 0} platser klara` : '';
    return (
        <button
            type="button"
            title={`${fmtDate(item.date)} · ${s.label}${roundText}${item.inspection_id == null ? ' · visa vad som ska kontrolleras' : ''}`}
            onClick={() => { if (item.inspection_id == null) onOpenPlanned(); else if (item.round_id) onOpenRound(item.round_id); else onOpen(item.inspection_id); }}
            style={{
                width: 22, height: 22, borderRadius: '50%', padding: 0, fontSize: 10, lineHeight: '20px', fontFamily: 'inherit',
                border: `2px solid ${s.color}`, background: s.hollow ? 'white' : s.color, color: s.hollow ? s.color : 'white',
                cursor: clickable ? 'pointer' : 'default', fontWeight: 600,
            }}
        >
            {Number(item.date.slice(8, 10))}
        </button>
    );
};

const YearTab: React.FC<Props> = ({ group, refreshKey, onOpen, onOpenRound, onOpenPlanned, onError }) => {
    const [year, setYear] = useState(() => new Date().getFullYear());
    const [rows, setRows] = useState<YearRow[]>([]);
    const [today, setToday] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        fetchInspectionYear(year)
            .then(d => { if (!cancelled) { setRows(d.rows); setToday(d.today); } })
            .catch(e => onError(e.message))
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [year, refreshKey, onError]);

    const visible = useMemo(() => rows.filter(r => inGroup(group, r.group)), [rows, group]);
    // Radera i grupper när alla grupper visas och det finns fler än en.
    const grouped = useMemo(() => {
        const map = new Map<string, YearRow[]>();
        for (const r of visible) map.set(r.group, [...(map.get(r.group) ?? []), r]);
        return Array.from(map.entries());
    }, [visible]);
    const showGroupRows = !group && grouped.length > 1;

    const currentMonth = today.startsWith(String(year)) ? monthOf(today) : -1;
    const doneCount = (r: YearRow) => r.items.filter(i => i.status === 'done' || i.status === 'deviation').length;

    const nameCell: React.CSSProperties = { ...ui.td, position: 'sticky', left: 0, background: 'white', zIndex: 1 };
    const monthCell = (m: number): React.CSSProperties => ({
        ...ui.td, textAlign: 'center', minWidth: 52, padding: '6px 4px',
        background: m === currentMonth ? '#f0f5ff' : undefined,
        borderLeft: '1px solid #f0f0f0',
    });

    return (
        <div className="year-wheel">
            <style>{`
                .year-wheel .yw-name { max-width: 240px; }
                @media (max-width: 767px) { .year-wheel .yw-name { max-width: 140px; } }
                @media print {
                    .nav-bar, .bottom-nav, .surveillance-tabs, .year-wheel .no-print { display: none !important; }
                    .year-wheel table { font-size: 11px; }
                    .year-wheel button { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                }
            `}</style>

            <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                <button onClick={() => setYear(y => y - 1)} style={ui.btnLightNeutral} aria-label="Föregående år">‹</button>
                <span style={{ fontSize: 20, fontWeight: 'bold', minWidth: 60, textAlign: 'center' }}>{year}</span>
                <button onClick={() => setYear(y => y + 1)} style={ui.btnLightNeutral} aria-label="Nästa år">›</button>
                {year !== new Date().getFullYear() && (
                    <button onClick={() => setYear(new Date().getFullYear())} style={ui.btnLight}>I år</button>
                )}
                <span style={{ flex: 1 }} />
                <button onClick={() => window.print()} style={ui.btnLightNeutral}>Skriv ut</button>
            </div>

            {loading && visible.length === 0 ? (
                <div style={{ ...ui.card, color: '#666' }}>Hämtar…</div>
            ) : visible.length === 0 ? (
                <div style={{ ...ui.card, color: '#666' }}>Inga scheman eller kontroller för {year}. Skapa ett schema under fliken Scheman så dyker tillfällena upp här.</div>
            ) : (
                <div style={{ overflowX: 'auto', background: 'white', borderRadius: ui.radius, border: '1px solid #e5e7eb' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={{ ...ui.th, position: 'sticky', left: 0, background: '#f8f9fa', zIndex: 2 }}>Schema</th>
                                {MONTHS.map((m, i) => (
                                    <th key={m} style={{ ...ui.th, textAlign: 'center', background: i === currentMonth ? '#e2ecff' : undefined, borderLeft: '1px solid #f0f0f0' }}>{m}</th>
                                ))}
                                <th style={{ ...ui.th, textAlign: 'center', whiteSpace: 'nowrap' }}>Gjorda</th>
                            </tr>
                        </thead>
                        <tbody>
                            {grouped.map(([g, list]) => (
                                <React.Fragment key={g}>
                                    {showGroupRows && (
                                        <tr>
                                            <td colSpan={14} style={{ ...ui.td, background: '#f8f9fa', fontWeight: 'bold', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em', color: '#555' }}>{g}</td>
                                        </tr>
                                    )}
                                    {list.map(r => (
                                        <tr key={r.key} style={{ opacity: r.active ? 1 : 0.55 }}>
                                            <td style={nameCell}>
                                                <div className="yw-name">
                                                <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.name}>{r.name}</div>
                                                <div style={{ fontSize: 11, color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {r.schedule_id == null ? 'Utan schema'
                                                        : [r.zone_name, r.interval_unit ? intervalText(r.interval_unit, r.interval_count ?? 1) : null, r.active ? null : 'pausat']
                                                            .filter(Boolean).join(' · ')}
                                                </div>
                                                </div>
                                            </td>
                                            {MONTHS.map((_, m) => (
                                                <td key={m} style={monthCell(m)}>
                                                    <div style={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                                                        {r.items.filter(i => monthOf(i.date) === m).map(i => (
                                                            <Dot key={i.date + (i.inspection_id ?? 'p')} item={i} onOpen={onOpen} onOpenRound={onOpenRound}
                                                                onOpenPlanned={() => onOpenPlanned({ name: r.name, date: i.date, zone_name: r.zone_name, template_id: r.template_id, template_name: r.template_name })} />
                                                        ))}
                                                    </div>
                                                </td>
                                            ))}
                                            <td style={{ ...ui.td, textAlign: 'center', fontSize: 12, color: '#555', whiteSpace: 'nowrap' }}>
                                                {doneCount(r)} av {r.items.length}
                                            </td>
                                        </tr>
                                    ))}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 12, fontSize: 12, color: '#555' }}>
                {LEGEND.map(k => {
                    const s = STATUS[k];
                    return (
                        <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                            <span style={{ width: 12, height: 12, borderRadius: '50%', border: `2px solid ${s.color}`, background: s.hollow ? 'white' : s.color, display: 'inline-block' }} />
                            {s.label}
                        </span>
                    );
                })}
                <span style={{ color: '#888' }}>Siffran i pricken är dagen i månaden. Klicka på en prick för att öppna kontrollen.</span>
            </div>
        </div>
    );
};

export default YearTab;
